import type { FunctionComponent } from "react";
import { useCallback, useEffect } from "react";
import { useSearchParams } from "react-router";
import useSWR from "swr";
import {
	ContentContainer,
	ContentContainerWrapper,
} from "~/components/ContentContainer";
import { DefaultLayout } from "~/components/Layout";
import { Loading } from "~/components/Loading";
import { ModalAction, ModalContent, useModal } from "~/components/Modal";
import { useSnackBar } from "~/components/SnackBar";
import type { ResponseError } from "~/error";
import { createFlowErrorHandler } from "~/utils/errorHandler";

interface Consent {
	client: Client;
	skip: boolean;
}

interface Client {
	id: string;
	name: string;
	grant_scope: string;
	logo_uri: string;
	policy_uri: string;
	tos_uri: string;
}
export const useConsent = (
	challenge: string,
	onError: (err: ResponseError) => void,
): [Consent | undefined, boolean] => {
	const { data, error, isLoading } = useSWR<Consent, ResponseError>(
		`/api/v2/oidc/consent?consent_challenge=${challenge}`,
	);

	useEffect(() => {
		if (error) {
			onError(error);
		}
	}, [error, onError]);
	return [data, isLoading];
};

const ignoreScope = [
	"t4_profile",
	"offline",
	"offline_access",
	"token_introspection",
];
const scopeDescriptionDict: { [key: string]: string } = {
	openid: "Your User ID for account federation.",
	profile: "Your basic information, including your name.",
	email: "Your email address.",
};

export const ConsentPage: FunctionComponent = () => {
	const [openModal, closeModal] = useModal();
	const [openSnackBar] = useSnackBar();

	const [searchParams] = useSearchParams();
	const challenge = searchParams.get("consent_challenge");

	// eslint-disable-next-line @typescript-eslint/no-unused-vars, @typescript-eslint/no-empty-function
	const [consent, isLoading] = useConsent(String(challenge), (_err) => {});

	/**
	 * biome-ignore lint: react-hooks/exhaustive-deps: if add openModal, closeModal to dependencies, will occur the following error
	 * Maximum update depth exceeded. This can happen when a component calls setState inside useEffect, but useEffect either doesn't have a dependency array, or one of the dependencies changes on every render.
	 * FIXME: fix design and correct dependencies
	 */
	const submit = useCallback(
		async (action: "accept" | "reject") => {
			try {
				const res = await fetch(
					`/api/v2/oidc/consent?consent_challenge=${challenge}`,
					{
						method: "POST",
						credentials: "same-origin",
						cache: "no-cache",
						body: JSON.stringify({ action }),
					},
				);
				const body = await res.json();
				if (res.status !== 200) {
					openModal({
						type: "error",
						title: "FAILED TO SUBMIT CONSENT REQUEST",
						children: (
							<>
								<ModalContent>
									<p className="text-center text-error">
										<h2 className="text-xl font-semibold mb-4">ERRORS</h2>
										<span>
											{body.error?.message ?? "Unexpected error occurred"}
										</span>
									</p>
								</ModalContent>
								<ModalAction>
									<button
										type="button"
										onClick={closeModal}
										className="btn btn-lg btn-t4-border rounded-full"
									>
										CLOSE
									</button>
								</ModalAction>
							</>
						),
					});
					return;
				}
				window.location.href = body.redirect_to;
			} catch (err) {
				createFlowErrorHandler(openSnackBar, openModal, closeModal)(err);
			}
		},
		[challenge],
	);

	useEffect(() => {
		if (consent?.skip) {
			submit("accept").then();
		}
	}, [consent, submit]);

	if (isLoading || consent?.skip) return <Loading />;
	if (!consent) return <p>Failed to load consent</p>;

	const scope = consent.client.grant_scope
		.split(" ")
		.filter((scope) => !ignoreScope.includes(scope))
		.map((scope) => scopeDescriptionDict[scope] ?? scope);

	return (
		<>
			<title>Consent | TIER IV Account</title>
			<DefaultLayout>
				<ContentContainerWrapper>
					<ContentContainer size="lg">
						<h1 className="mt-10 text-center font-normal">
							PERMISSION REQUEST
						</h1>
						{consent.client.logo_uri && (
							<div className="mt-8 text-center">
								<img
									src={consent.client.logo_uri}
									alt={consent.client.name}
									className="inline-block logo-img"
								/>
							</div>
						)}
						<section className="mt-8">
							<p className="text-xl">
								{consent.client.name} would like to access
							</p>
							<ul className="mt-8">
								{scope.map((scope) => (
									<li key={scope} className="list-disc ml-6">
										{scope}
									</li>
								))}
							</ul>
							<div className="flex flex-col items-center md:flex-row md:justify-center md:gap-3 mt-16">
								<button
									type="button"
									className="btn short btn-t4-primary rounded-full w-4/12"
									onClick={() => submit("accept")}
								>
									ALLOW
								</button>
								<button
									type="button"
									className="btn short btn-t4-danger rounded-full w-4/12"
									onClick={() => submit("reject")}
								>
									DENY
								</button>
							</div>
						</section>
					</ContentContainer>
				</ContentContainerWrapper>
			</DefaultLayout>
		</>
	);
};
